/**
 * delivery-date.js — the cutoff / transit / blackout maths behind the cart's
 * delivery-date picker.
 *
 * Bemka ships perishable caviar overnight against a same-day order cutoff. If
 * this maths is wrong the store promises a date it cannot hit and the product
 * arrives warm. The failure is silent — no exception, no error page — so this
 * module is deliberately kept PURE, DEPENDENCY-FREE and DOM-FREE: every
 * function takes its inputs as arguments and returns a value. That is what
 * lets `tests/delivery-date.test.js` exercise it under plain Node with
 * `node --test`, including in forced foreign timezones.
 *
 * Ported from `getEarliestDeliveryDate()` in the Next.js storefront's
 * src/app/cart/cart-client.tsx, with two bugs in that source deliberately NOT
 * carried across:
 *
 *   1. The source did `new Date(new Date().toLocaleString('en-US', { timeZone }))`.
 *      That formats to a US date string and re-parses it in the RUNTIME's zone,
 *      not the store's. It happens to read the right wall-clock hour, but the
 *      Date it produces is wrong for any absolute-time use and the round-trip
 *      is locale- and implementation-dependent.
 *
 *   2. The source derived blackout keys with `date.toISOString().split('T')[0]`,
 *      i.e. in UTC. For a shopper east of UTC, local midnight falls on the
 *      PREVIOUS UTC day, so blackout dates were silently off by one and the
 *      store accepted orders for a day it does not ship.
 *
 * Both are fixed the same way: all calendar reasoning happens on a "civil date"
 * (a bare year/month/day with no zone attached), read out of the shopper's
 * instant with `Intl.DateTimeFormat` and an EXPLICIT store timeZone. A fixed
 * UTC offset is never used, because that breaks across the DST transitions in
 * March and November.
 *
 * Civil dates are represented internally as the epoch-milliseconds of UTC
 * midnight on that day. That is a convenient carrier, not a real instant: UTC
 * has no DST, so `+ 86_400_000` is always exactly "the next day". The value is
 * converted back to a real instant exactly once, at the very end, by
 * `instantAtStoreHour()`.
 *
 * The module is loaded in the theme as `<script type="module">`; it also hangs
 * its API on `window.BemkaDeliveryDate` so the classic `cart.js` script can
 * reach it without an import map. Under Node there is no `window`, so the
 * assignment is guarded.
 */

const DAY_MS = 86400000;

/** Absolute ceiling on any day-walking loop, so a pathological config (every
 *  day blacked out) can never spin the browser. Always paired with a calendar
 *  bound derived from `maxDeliveryDays`. */
const MAX_STEPS = 500;

/**
 * Defaults mirror src/config/shipping.json in the storefront being replaced.
 * The cart section overrides every one of these from theme settings; these
 * values are the safety net for a malformed or missing settings payload.
 */
export const DEFAULT_SHIPPING_CONFIG = Object.freeze({
  cutoffHour: 12,
  cutoffMinute: 0,
  timezone: 'America/New_York',
  transitDays: 1,
  shipOnSaturday: false,
  shipOnSunday: false,
  deliverOnSunday: false,
  deliverOnMonday: false,
  maxDeliveryDays: 90,
  blackoutDates: [],
});

/* -------------------------------------------------------------------------
 * Timezone primitives
 * ---------------------------------------------------------------------- */

function toBoolean(value, fallback) {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

function toInteger(value, fallback) {
  const n = typeof value === 'number' ? value : parseInt(value, 10);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

function isUsableTimezone(timeZone) {
  if (typeof timeZone !== 'string' || timeZone === '') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date(0));
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Read an instant's wall-clock parts in a named IANA zone.
 *
 * `hourCycle: 'h23'` matters: with `hour12: false` some engines report midnight
 * as hour 24, which would silently push every midnight order past a noon cutoff.
 *
 * @param {Date} date
 * @param {string} timeZone
 * @returns {{year: number, month: number, day: number, hour: number, minute: number, second: number}}
 */
export function getZonedParts(date, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const out = { year: 0, month: 1, day: 1, hour: 0, minute: 0, second: 0 };
  for (const part of parts) {
    if (part.type in out) out[part.type] = Number(part.value);
  }
  if (out.hour === 24) out.hour = 0;
  return out;
}

/**
 * The zone's UTC offset, in milliseconds, AT a given instant. Derived by
 * formatting the instant into the zone and treating those wall-clock parts as
 * if they were UTC — the difference is the offset. This is what makes the
 * module DST-correct without a timezone database.
 *
 * @param {Date} date
 * @param {string} timeZone
 * @returns {number}
 */
function zoneOffsetMs(date, timeZone) {
  const p = getZonedParts(date, timeZone);
  const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  // Drop sub-second precision: formatToParts has none, so keeping it would
  // leak a spurious few hundred milliseconds into the offset.
  return asIfUtc - Math.floor(date.getTime() / 1000) * 1000;
}

/**
 * Turn a civil date into a real instant at a given store-local hour.
 *
 * Noon is the hour we use everywhere. It is the safest choice: DST transitions
 * happen in the small hours, so noon local never lands in a skipped or repeated
 * wall-clock window, and formatting the result back into the store zone always
 * yields the civil date we started from.
 *
 * The offset is applied, then re-checked once and re-applied if the first guess
 * landed on the far side of a transition.
 *
 * @param {number} civilMs - UTC midnight of the civil date.
 * @param {number} hour - Store-local hour to anchor at.
 * @param {string} timeZone
 * @returns {Date}
 */
function instantAtStoreHour(civilMs, hour, timeZone) {
  const civil = new Date(civilMs);
  const target = Date.UTC(
    civil.getUTCFullYear(),
    civil.getUTCMonth(),
    civil.getUTCDate(),
    hour
  );

  let timestamp = target - zoneOffsetMs(new Date(target), timeZone);
  const refined = target - zoneOffsetMs(new Date(timestamp), timeZone);
  if (refined !== timestamp) timestamp = refined;

  return new Date(timestamp);
}

/* -------------------------------------------------------------------------
 * Civil-date helpers
 * ---------------------------------------------------------------------- */

/** The civil date an instant falls on, in the store's zone. */
function civilOf(date, timeZone) {
  const p = getZonedParts(date, timeZone);
  return Date.UTC(p.year, p.month - 1, p.day);
}

/** `YYYY-MM-DD` for a civil date. Never goes through toISOString. */
function civilKey(civilMs) {
  const d = new Date(civilMs);
  const year = String(d.getUTCFullYear()).padStart(4, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** 0 = Sunday … 6 = Saturday, for a civil date. */
function civilWeekday(civilMs) {
  return new Date(civilMs).getUTCDay();
}

/**
 * `YYYY-MM-DD` for an instant, read in the given zone. Exported because the
 * calendar UI compares days as strings — string comparison of ISO keys is a
 * total order and sidesteps every instant-comparison trap.
 *
 * @param {Date} date
 * @param {string} timeZone
 * @returns {string}
 */
export function getDateKey(date, timeZone) {
  return civilKey(civilOf(date, timeZone));
}

/**
 * Build the instant that represents a calendar cell. Month is 1-12 so callers
 * never have to remember JavaScript's zero-based months.
 *
 * @param {number} year
 * @param {number} month - 1-12
 * @param {number} day
 * @param {string} timeZone
 * @returns {Date}
 */
export function makeStoreDate(year, month, day, timeZone) {
  const zone = isUsableTimezone(timeZone) ? timeZone : DEFAULT_SHIPPING_CONFIG.timezone;
  return instantAtStoreHour(Date.UTC(year, month - 1, day), 12, zone);
}

/* -------------------------------------------------------------------------
 * Config
 * ---------------------------------------------------------------------- */

/**
 * Coerce whatever the theme settings produced into a complete, sane config.
 * Never mutates its argument — the unit tests assert that explicitly, because a
 * function that quietly rewrites the caller's config is impossible to reason
 * about when it is called once per calendar cell.
 *
 * @param {object} [config]
 * @returns {{cutoffHour: number, cutoffMinute: number, timezone: string, transitDays: number, shipOnSaturday: boolean, shipOnSunday: boolean, deliverOnSunday: boolean, deliverOnMonday: boolean, maxDeliveryDays: number, blackoutDates: string[]}}
 */
export function normalizeShippingConfig(config) {
  const raw = config && typeof config === 'object' ? config : {};
  const d = DEFAULT_SHIPPING_CONFIG;

  const blackoutDates = Array.isArray(raw.blackoutDates)
    ? raw.blackoutDates
        .map((value) => String(value).trim())
        .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))
    : [];

  const timezone = isUsableTimezone(raw.timezone) ? raw.timezone : d.timezone;

  return {
    cutoffHour: Math.min(23, Math.max(0, toInteger(raw.cutoffHour, d.cutoffHour))),
    cutoffMinute: Math.min(59, Math.max(0, toInteger(raw.cutoffMinute, d.cutoffMinute))),
    timezone,
    transitDays: Math.min(30, Math.max(0, toInteger(raw.transitDays, d.transitDays))),
    shipOnSaturday: toBoolean(raw.shipOnSaturday, d.shipOnSaturday),
    shipOnSunday: toBoolean(raw.shipOnSunday, d.shipOnSunday),
    deliverOnSunday: toBoolean(raw.deliverOnSunday, d.deliverOnSunday),
    deliverOnMonday: toBoolean(raw.deliverOnMonday, d.deliverOnMonday),
    maxDeliveryDays: Math.min(365, Math.max(1, toInteger(raw.maxDeliveryDays, d.maxDeliveryDays))),
    blackoutDates,
  };
}

/** Parse a JSON config string (as carried on a data attribute). Never throws. */
export function parseShippingConfig(json) {
  if (typeof json !== 'string' || json.trim() === '') {
    return normalizeShippingConfig(null);
  }
  try {
    return normalizeShippingConfig(JSON.parse(json));
  } catch (error) {
    return normalizeShippingConfig(null);
  }
}

/* -------------------------------------------------------------------------
 * The rules
 * ---------------------------------------------------------------------- */

/**
 * A civil date the carrier will not deliver on. Mirrors `isNoDeliveryDay()` in
 * the source exactly: Sunday and Monday are gated by their own flags (Bemka's
 * overnight product must not sit in a depot over a weekend), and any blackout
 * date is out. Saturday delivery is deliberately allowed — the source has no
 * `deliverOnSaturday` flag and adding one here would change behaviour.
 */
function isNoDeliveryCivil(civilMs, config, blackoutSet) {
  const weekday = civilWeekday(civilMs);
  if (weekday === 0 && !config.deliverOnSunday) return true;
  if (weekday === 1 && !config.deliverOnMonday) return true;
  return blackoutSet.has(civilKey(civilMs));
}

/**
 * Public predicate for the calendar: can this instant be a delivery date?
 *
 * @param {Date} date
 * @param {object} config
 * @returns {boolean}
 */
export function isNoDeliveryDay(date, config) {
  const cfg = normalizeShippingConfig(config);
  return isNoDeliveryCivil(civilOf(date, cfg.timezone), cfg, new Set(cfg.blackoutDates));
}

/**
 * The earliest date the store can promise, for an order placed at `now`.
 *
 * 1. Read the store-local wall clock and decide whether we are past the cutoff.
 *    Exactly on the cutoff minute counts as PAST it (`minute >= cutoffMinute`),
 *    which is the source's semantics and the safe direction to round.
 * 2. Pick the ship date — today if we made the cutoff, otherwise tomorrow —
 *    then walk forward past any weekend the warehouse does not ship on.
 * 3. Add `transitDays` calendar days, not counting days the carrier will not
 *    deliver on: those days are stepped over without consuming transit.
 * 4. Step over any remaining no-delivery day the transit walk landed on.
 *
 * The search is bounded twice over — by a calendar limit derived from
 * `maxDeliveryDays` and by a hard step count — so a config that blacks out
 * every day returns `null` rather than hanging the browser.
 *
 * @param {Date|number|string} now - The instant the order is being placed.
 * @param {object} [config] - A shipping config; see DEFAULT_SHIPPING_CONFIG.
 * @returns {Date|null} An instant at store-local noon on the delivery date, or
 *   null when no date within the booking window is deliverable.
 */
export function getEarliestDeliveryDate(now, config) {
  const cfg = normalizeShippingConfig(config);
  const blackoutSet = new Set(cfg.blackoutDates);

  const reference = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(reference.getTime())) return null;

  const parts = getZonedParts(reference, cfg.timezone);
  const pastCutoff =
    parts.hour > cfg.cutoffHour ||
    (parts.hour === cfg.cutoffHour && parts.minute >= cfg.cutoffMinute);

  const todayCivil = Date.UTC(parts.year, parts.month - 1, parts.day);

  // How far ahead we are willing to look. `maxDeliveryDays` is the customer-
  // facing booking window; the transit allowance keeps a sane config with a
  // very short window from being reported as undeliverable.
  const horizonDays = Math.max(cfg.maxDeliveryDays, cfg.transitDays + 14);
  const limitCivil = todayCivil + horizonDays * DAY_MS;

  let shipCivil = pastCutoff ? todayCivil + DAY_MS : todayCivil;

  // Advance to the next day the warehouse actually ships.
  for (let step = 0; ; step += 1) {
    if (step > MAX_STEPS || shipCivil > limitCivil) return null;
    const weekday = civilWeekday(shipCivil);
    if (weekday === 0 && !cfg.shipOnSunday) {
      shipCivil += DAY_MS;
      continue;
    }
    if (weekday === 6 && !cfg.shipOnSaturday) {
      shipCivil += DAY_MS;
      continue;
    }
    break;
  }

  // Consume transit days, stepping over days the carrier will not deliver on.
  let deliveryCivil = shipCivil;
  let remaining = cfg.transitDays;
  let steps = 0;
  while (remaining > 0) {
    deliveryCivil += DAY_MS;
    steps += 1;
    if (steps > MAX_STEPS || deliveryCivil > limitCivil) return null;
    if (isNoDeliveryCivil(deliveryCivil, cfg, blackoutSet)) continue;
    remaining -= 1;
  }

  // With transitDays 0 — or a blackout that starts on the landing day — the
  // date we arrived at may still be undeliverable. Walk it forward.
  steps = 0;
  while (isNoDeliveryCivil(deliveryCivil, cfg, blackoutSet)) {
    deliveryCivil += DAY_MS;
    steps += 1;
    if (steps > MAX_STEPS || deliveryCivil > limitCivil) return null;
  }

  return instantAtStoreHour(deliveryCivil, 12, cfg.timezone);
}

/**
 * The last date the calendar will offer: `maxDeliveryDays` after today, read in
 * store time. The source computed this in the shopper's local timezone, which
 * moved the far edge of the calendar around by a day depending on where the
 * shopper was sitting.
 *
 * @param {Date|number|string} now
 * @param {object} [config]
 * @returns {Date}
 */
export function getMaxDeliveryDate(now, config) {
  const cfg = normalizeShippingConfig(config);
  const reference = now instanceof Date ? now : new Date(now);
  const base = Number.isNaN(reference.getTime()) ? new Date() : reference;
  const todayCivil = civilOf(base, cfg.timezone);
  return instantAtStoreHour(todayCivil + cfg.maxDeliveryDays * DAY_MS, 12, cfg.timezone);
}

/**
 * Format a delivery date in the store's timezone.
 *
 * The `long` style is the exact shape the Next.js storefront wrote into the
 * "Delivery Date" cart attribute ("Friday, September 4, 2026"), and warehouse
 * staff read that string off the order in Shopify admin. Changing it would
 * break a human process, so it is pinned here rather than left to the browser
 * locale.
 *
 * @param {Date} date
 * @param {string} timeZone
 * @param {'long'|'short'|'month'} [style]
 * @returns {string}
 */
export function formatDeliveryDate(date, timeZone, style) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const zone = isUsableTimezone(timeZone) ? timeZone : DEFAULT_SHIPPING_CONFIG.timezone;

  let options;
  if (style === 'short') {
    options = { weekday: 'short', month: 'short', day: 'numeric' };
  } else if (style === 'month') {
    options = { month: 'long', year: 'numeric' };
  } else {
    options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  }

  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: zone, ...options }).format(date);
  } catch (error) {
    return '';
  }
}

const api = {
  DEFAULT_SHIPPING_CONFIG,
  formatDeliveryDate,
  getDateKey,
  getEarliestDeliveryDate,
  getMaxDeliveryDate,
  getZonedParts,
  isNoDeliveryDay,
  makeStoreDate,
  normalizeShippingConfig,
  parseShippingConfig,
};

// The theme loads this file as a module; cart.js is a classic deferred script
// and reads the API off `window`. Both run before DOMContentLoaded, and cart.js
// initialises on that event, so ordering is guaranteed without an import map.
if (typeof window !== 'undefined') {
  window.BemkaDeliveryDate = api;
}

export default api;
